// src/hooks/useBroadcastSync.ts
// Hook de coordination inter-onglets pour la régie.
//
// Permet à un seul onglet à la fois de "posséder" le deck ou le multiview
// pour un UUID donné, afin d'éviter de doubler les connexions signaling/WebRTC.
//
// Usage côté onglet principal (LiveStreamManager) :
//   const { isDeckExternal, isMultiviewExternal } = useBroadcastSync(uuid, 'main');
//   → isDeckExternal = true quand un onglet externe a pris le contrôle du deck
//
// Usage côté onglet externe (DeckFullscreenPage) :
//   const { claimControl, releaseControl } = useBroadcastSync(uuid, 'deck');
//   → claimControl() au montage, releaseControl() au démontage

import { useState, useEffect, useRef, useCallback } from 'react';

export type BroadcastRole = 'main' | 'deck' | 'multiview';

interface BroadcastMessage {
  type    : 'take-control' | 'release-control' | 'ping' | 'pong';
  target  : 'deck' | 'multiview';
  sender  : BroadcastRole;
  uuid    : string;
}

interface UseBroadcastSyncReturn {
  /** true quand un onglet externe contrôle le deck */
  isDeckExternal      : boolean;
  /** true quand un onglet externe contrôle le multiview */
  isMultiviewExternal : boolean;
  /** Prendre le contrôle (appelé par l'onglet externe au montage) */
  claimControl        : () => void;
  /** Libérer le contrôle (appelé par l'onglet externe au démontage) */
  releaseControl      : () => void;
}

export function useBroadcastSync(
  uuid: string | undefined,
  role: BroadcastRole,
): UseBroadcastSyncReturn {

  const [isDeckExternal, setIsDeckExternal]           = useState(false);
  const [isMultiviewExternal, setIsMultiviewExternal] = useState(false);
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Créer / détruire le channel
  useEffect(() => {
    if (!uuid) return;
    const ch = new BroadcastChannel(`mediartc-regie-${uuid}`);
    channelRef.current = ch;

    ch.onmessage = (e: MessageEvent<BroadcastMessage>) => {
      const msg = e.data;
      if (!msg || msg.uuid !== uuid) return;

      if (role === 'main') {
        // L'onglet principal écoute les prises/libérations de contrôle
        if (msg.type === 'take-control') {
          if (msg.target === 'deck')      setIsDeckExternal(true);
          if (msg.target === 'multiview') setIsMultiviewExternal(true);
        }
        if (msg.type === 'release-control') {
          if (msg.target === 'deck')      setIsDeckExternal(false);
          if (msg.target === 'multiview') setIsMultiviewExternal(false);
        }
      }

      // Répondre aux pings (permet à un onglet de vérifier si un main est actif)
      if (msg.type === 'ping' && role === 'main') {
        ch.postMessage({
          type: 'pong', target: msg.target,
          sender: 'main', uuid,
        } satisfies BroadcastMessage);
      }
    };

    return () => {
      ch.close();
      channelRef.current = null;
    };
  }, [uuid, role]);

  // Quand l'onglet externe se ferme, libérer le contrôle
  useEffect(() => {
    if (role === 'main' || !uuid) return;
    const target = role as 'deck' | 'multiview';

    const handleUnload = () => {
      // BroadcastChannel.postMessage est synchrone et survit au beforeunload
      channelRef.current?.postMessage({
        type: 'release-control', target,
        sender: role, uuid,
      } satisfies BroadcastMessage);
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      handleUnload(); // aussi au démontage React
    };
  }, [uuid, role]);

  const claimControl = useCallback(() => {
    if (!uuid || role === 'main') return;
    const target = role as 'deck' | 'multiview';
    channelRef.current?.postMessage({
      type: 'take-control', target,
      sender: role, uuid,
    } satisfies BroadcastMessage);
  }, [uuid, role]);

  const releaseControl = useCallback(() => {
    if (!uuid || role === 'main') return;
    const target = role as 'deck' | 'multiview';
    channelRef.current?.postMessage({
      type: 'release-control', target,
      sender: role, uuid,
    } satisfies BroadcastMessage);
  }, [uuid, role]);

  return { isDeckExternal, isMultiviewExternal, claimControl, releaseControl };
}
